import React, { useMemo, useState, useEffect } from 'react';
import { Modal, Input, Select, Table, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useGetVwMatClntMatPipingItems } from '../../hooks/genericHooks';
import type { VwMatClntMatPipingItem } from '../../types';

export type ClntPipeMatRow = {
  itemcode: string;
  description: string;
  size_text?: string | null;
  unit?: string | null;
};

export interface ClntPipeMatSelectProps {
  open: boolean;
  onOk: (rows: ClntPipeMatRow[]) => void;
  onCancel: () => void;
  title?: string;
  mode?: 'single' | 'multiple';
}

const norm = (x: any) => (x == null ? '' : String(x)).trim();
const lower = (x: any) => norm(x).toLowerCase();

const ClntPipeMatSelect: React.FC<ClntPipeMatSelectProps> = ({ open, onOk, onCancel, title, mode = 'multiple' }) => {
  const { data = [], isLoading } = useGetVwMatClntMatPipingItems() as any;
  const rows: VwMatClntMatPipingItem[] = Array.isArray(data) ? data : (data?.data ?? []);

  // Filters
  const [search, setSearch] = useState(''); // description search supports multi-word AND and quoted phrases
  const [unitFilter, setUnitFilter] = useState<string>('');
  const [sizeFilter, setSizeFilter] = useState<string>('');

  // Selection state
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // Pagination state (mirror reference modal)
  const [current, setCurrent] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(100);

  // First stage: filter by search only (used to cascade options)
  const searchFiltered = useMemo(() => {
    const sv = lower(search);
    const phraseMatches = Array.from(sv.matchAll(/"([^\"]+)"/g)).map(m => m[1].trim()).filter(Boolean);
    const remaining = sv.replace(/"[^\"]+"/g, ' ').trim();
    const tokens = remaining.split(/\s+/).filter(Boolean);
    const matchesSearchOnly = (r: VwMatClntMatPipingItem) => {
      if (tokens.length === 0 && phraseMatches.length === 0) return true;
      const hay = `${r.itemcode} ${r.description || ''} ${r.size_text || ''} ${r.unit || ''}`.toLowerCase();
      if (phraseMatches.length > 0) return phraseMatches.every(p => hay.includes(p));
      return tokens.every(t => hay.includes(t));
    };
    return (rows || []).filter(matchesSearchOnly);
  }, [rows, search]);

  // Build filter option lists cascading with the OTHER active filter and search
  const unitOptions = useMemo(() => {
    const set = new Map<string, string>();
    (searchFiltered || [])
      .filter(r => (!sizeFilter || lower(r.size_text) === lower(sizeFilter)))
      .forEach(r => { const raw = norm(r.unit); if (raw) { const k = lower(raw); if (!set.has(k)) set.set(k, raw); } });
    return Array.from(set.values()).sort().map(v => ({ value: v, label: v }));
  }, [searchFiltered, sizeFilter]);

  const sizeOptions = useMemo(() => {
    const set = new Map<string, string>();
    (searchFiltered || [])
      .filter(r => (!unitFilter || lower(r.unit) === lower(unitFilter)))
      .forEach(r => { const raw = norm(r.size_text); if (raw) { const k = lower(raw); if (!set.has(k)) set.set(k, raw); } });
    return Array.from(set.values()).sort().map(v => ({ value: v, label: v }));
  }, [searchFiltered, unitFilter]);

  const filtered = useMemo(() => {
    return (searchFiltered || [])
      .filter(r => (!unitFilter || lower(r.unit) === lower(unitFilter)))
      .filter(r => (!sizeFilter || lower(r.size_text) === lower(sizeFilter)));
  }, [searchFiltered, unitFilter, sizeFilter]);

  // Reset to first page when filters/search change
  useEffect(() => { setCurrent(1); }, [search, unitFilter, sizeFilter]);

  const dataSource = useMemo(() => (filtered || []), [filtered]);

  const fullMap = useMemo(() => {
    const m = new Map<string, VwMatClntMatPipingItem>();
    (rows || []).forEach((r) => m.set(r.itemcode, r));
    return m;
  }, [rows]);

  const columns: ColumnsType<VwMatClntMatPipingItem> = [
    { title: 'Item Code', dataIndex: 'itemcode', key: 'itemcode', width: 140 },
    { title: 'Description', dataIndex: 'description', key: 'description', width:280, ellipsis: true },
    { title: 'Size', dataIndex: 'size_text', key: 'size_text', width: 100 },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 50 },
  ];

  const handleOk = () => {
    const rows = (selectedRowKeys || [])
      .map(k => fullMap.get(String(k)))
      .filter((r): r is VwMatClntMatPipingItem => !!r)
      .map((r) => ({ itemcode: r.itemcode, description: r.description, size_text: r.size_text ?? null, unit: r.unit ?? null }));
    onOk(rows);
  };

  const onRowDoubleClick = (record: any) => {
    const out: ClntPipeMatRow = { itemcode: record.itemcode, description: record.description, size_text: record.size_text ?? null, unit: record.unit ?? null };
    onOk([out]);
  };

  return (
    <Modal
      open={open}
      title={title || 'Select Pipe Material'}
      onOk={handleOk}
      onCancel={onCancel}
      okText={mode === 'multiple' ? 'Add Selected' : 'Select'}
      width={680}
      bodyStyle={{ paddingInline: 4, paddingBlock: 4 }}
    >
      <Space style={{ marginBottom: 8 }} wrap>
        <Input
          placeholder='Search description/item (multi-word AND, use "phrases")'
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 200 }}
        />
        <Select
          placeholder="Unit"
          allowClear
          value={unitFilter || undefined}
          onChange={(v) => setUnitFilter(v || '')}
          style={{ width: 100 }}
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) => (String(option?.label || '')).toLowerCase().includes(input.toLowerCase())}
          options={unitOptions}
        />
        <Select
          placeholder="Size"
          allowClear
          value={sizeFilter || undefined}
          onChange={(v) => setSizeFilter(v || '')}
          style={{ width: 140 }}
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) => (String(option?.label || '')).toLowerCase().includes(input.toLowerCase())}
          options={sizeOptions}
        />
      </Space>
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey={(r) => r.itemcode}
        loading={isLoading}
        size="small"
        pagination={{
          current,
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: [50, 100, 200, 500],
          showTotal: (total, [start, end]) => `${start}-${end} • Filtered: ${total.toLocaleString()}`,
          onChange: (p, ps) => { setCurrent(p); setPageSize(ps); },
        }}
        tableLayout="fixed"
        rowSelection={{
          type: mode === 'single' ? 'radio' : 'checkbox',
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys as React.Key[]),
          columnWidth: 30,
          preserveSelectedRowKeys: true,
        }}
        onRow={(record) => ({ onDoubleClick: () => onRowDoubleClick(record) })}
        scroll={{ y: 420 }}
      />
    </Modal>
  );
};

export default ClntPipeMatSelect;
